import React, {
  createContext, memo, useContext, useMemo,
} from 'react';
import { highlightSearchMatch } from '@common/utils/highlight-search-text';

export type FeedSearchHighlightField = 'content' | 'author' | 'mention';

interface FeedSearchHighlightValue {
  query: string;
  scope: string;
}

const EMPTY_HIGHLIGHT: FeedSearchHighlightValue = { query: '', scope: 'all' };

const FeedSearchHighlightContext = createContext<FeedSearchHighlightValue>(EMPTY_HIGHLIGHT);

interface FeedSearchHighlightProviderProps {
  query: string;
  scope?: string;
  children: React.ReactNode;
}

export const FeedSearchHighlightProvider = memo(({
  query,
  scope = 'all',
  children,
}: FeedSearchHighlightProviderProps) => {
  const value = useMemo(() => ({ query, scope }), [query, scope]);

  return (
    <FeedSearchHighlightContext.Provider value={value}>
      {children}
    </FeedSearchHighlightContext.Provider>
  );
});

FeedSearchHighlightProvider.displayName = 'FeedSearchHighlightProvider';

export function useFeedSearchHighlight() {
  return useContext(FeedSearchHighlightContext);
}

function shouldHighlightField(scope: string, field: FeedSearchHighlightField) {
  if (scope === 'posted_by') return field === 'author';
  if (scope === 'mentioned_in') return field === 'mention';
  return true;
}

interface SearchHighlightProps {
  text?: string | null;
  field?: FeedSearchHighlightField;
}

const SearchHighlight = memo(({ text, field = 'content' }: SearchHighlightProps) => {
  const { query, scope } = useFeedSearchHighlight();
  const activeQuery = shouldHighlightField(scope, field) ? query : '';
  const parts = useMemo(() => {
    let offset = 0;
    return highlightSearchMatch(text || '', activeQuery).map((part) => {
      const value = typeof part === 'string' ? part : part.match;
      const key = `${offset}-${value}`;
      offset += value.length;
      return { value, isMatch: typeof part !== 'string', key };
    });
  }, [text, activeQuery]);

  if (!text) return null;

  return (
    <>
      {parts.map((part) => (
        part.isMatch
          ? <mark key={part.key} className="FeedSearchHighlight">{part.value}</mark>
          : part.value
      ))}
    </>
  );
});

SearchHighlight.displayName = 'SearchHighlight';

export default SearchHighlight;
